import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, switchMap, takeWhile, withLatestFrom } from "rxjs";

// Store
import { Store } from "@ngrx/store";
import * as SportsbookActions from "../../../stores/sportsbook/sportsbook.actions";
import { SportsbookFacade } from "../../../stores/sportsbook/sportsbook.facade";
import { SportsbookState } from "../../../stores/sportsbook/sportsbook.reducers";

// Services
import { BufferFacade } from "@shared-providers/facades";
import { SportTrackPollingService } from "@sports-services/index";
import { SPORTS_NAVIGATION_SERVICE_ERROR_CODES } from "@sports-services/sports.navigation.service";

import { SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION } from "@sports-services/sports.navigation.service/sports.navigations.constants";
// Utils
import { SportRaceService } from "@sports-services/sports.race.service";

@Injectable({ providedIn: "root" })
export class SportsbookRaceMarketEffects {
  // Services
  actions$ = inject(Actions);
  bufferFacade = inject(BufferFacade);
  sportRaceService = inject(SportRaceService);

  // Utils
  store = inject(Store<SportsbookState>);
  sportsbookFacade = inject(SportsbookFacade);
  sportTrackPollingService = inject(SportTrackPollingService);

  timer = {
    live: "live",
    regular: "regular",
    highlights: "highlights",
  };

  // RACE MARKET
  initRaceMarket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.initRaceMarket),
      withLatestFrom(this.store.select("sportsbook")),
      switchMap(([raceMarketParms, sportsbook]) => {
        const raceMarketPageData = sportsbook["raceMarketData"];
        this.sportTrackPollingService.stopAllPollings();
        return this.sportRaceService.getNextRaces(raceMarketParms.SportHandle).pipe(
          map((data) => {
            if (!data.length) {
              this.sportsbookFacade.setSportsError({
                errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.EMPTY_DATA_WARNING_ERROR,
                errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_19,
              });
            }
            const dropListEventSelected = data.find((i) => i.NodeId === raceMarketParms.NodeId);
            return SportsbookActions.fetchRaceMarket({
              filters: raceMarketPageData.filters,
              selectedFilter: raceMarketPageData.selectedFilter
                ? raceMarketPageData.selectedFilter
                : raceMarketPageData.filters[0],
              dropListEvents: data,
              dropListEventSelected: dropListEventSelected ? dropListEventSelected : raceMarketParms,
              participants: [],
            });
          }),
          catchError((e) => {
            this.sportsbookFacade.setSportsError({
              errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_ERROR,
              errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_20,
            });
            return [];
          }),
        );
      }),
    ),
  );

  setRaceMarketSelectedRace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.setRaceMarketSelectedRace),
      withLatestFrom(this.store.select("sportsbook")),
      map(([raceMarketParms, sportsbook]) => {
        const raceMarketPageData = sportsbook["raceMarketData"];
        return SportsbookActions.fetchRaceMarket({
          filters: raceMarketPageData.filters,
          selectedFilter: null,
          dropListEvents: raceMarketPageData.dropListEvents,
          dropListEventSelected: raceMarketParms,
          participants: [],
        });
      }),
    ),
  );

  fetchRaceMarket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchRaceMarket),
      switchMap((raceMarketData) => {
        this.sportTrackPollingService.startRaceMarketPagePolling();
        const raceMarketParams = {
          paddockId: raceMarketData.dropListEventSelected.PaddockId,
          raceId: raceMarketData.dropListEventSelected.NodeId,
          sport: raceMarketData.dropListEventSelected.SportHandle,
        };
        return this.bufferFacade
          .newAuxBuffer(
            this.sportRaceService,
            "getRaceMarketData",
            raceMarketParams,
            this.timer.live,
            this.sportTrackPollingService.raceMarketPolling$,
          )
          .pipe(
            takeWhile((_) => this.sportTrackPollingService.raceMarketPolling$.getValue()),
            map(([prev, current]) => {
              const dropListEventSelected = current.dropListEvents.find(
                (i) => i.NodeId === raceMarketParams.raceId,
              );
              if (!current.participants) {
                this.sportsbookFacade.setSportsError({
                  errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.EMPTY_DATA_ERROR,
                  errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_21,
                });
              }
              return SportsbookActions.setRaceMarket({
                filters: raceMarketData.filters,
                selectedFilter: raceMarketData.selectedFilter,
                dropListEvents: raceMarketData.dropListEvents,
                dropListEventSelected,
                participants: current.participants,
              });
            }),
            catchError((e) => {
              this.sportsbookFacade.setSportsError({
                errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_POLLING_ERROR,
                errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_22,
              });
              return [];
            }),
          );
      }),
    ),
  );
}
